import { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import theme from '../../assets/theme';

interface Props {
  analyserNode: AnalyserNode | null;
  isPlaying: boolean;
}

const BAR_COUNT = 3;
const BAR_WIDTH = 4;
const BAR_GAP = 3;
const MIN_BAR_HEIGHT = 3;
const CANVAS_HEIGHT = 17;
const SMOOTHING = 0.3;

/** `roundRect` exists at runtime but may be missing from older `lib.dom` typings. */
type Canvas2DWithRoundRect = CanvasRenderingContext2D & {
  roundRect?(
    x: number,
    y: number,
    w: number,
    h: number,
    radii: number | Iterable<number>,
  ): void;
};

function fillRoundedBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const c = ctx as Canvas2DWithRoundRect;
  ctx.beginPath();
  if (typeof c.roundRect === 'function') {
    c.roundRect(x, y, width, height, radius);
  } else {
    ctx.rect(x, y, width, height);
  }
  ctx.fill();
}

const WaveformVisualizer = ({ analyserNode, isPlaying }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const prevHeightsRef = useRef<number[]>(new Array(BAR_COUNT).fill(MIN_BAR_HEIGHT));

  const canvasWidth = BAR_COUNT * (BAR_WIDTH + BAR_GAP) - BAR_GAP;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !analyserNode) return;

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
    analyserNode.getByteFrequencyData(dataArray);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const prevHeights = prevHeightsRef.current;
    const binSize = Math.floor(dataArray.length / BAR_COUNT);

    for (let i = 0; i < BAR_COUNT; i++) {
      // Sample frequency bins evenly
      let sum = 0;
      for (let j = 0; j < binSize; j++) {
        sum += dataArray[i * binSize + j];
      }
      const avg = sum / binSize;

      const targetHeight = Math.max(MIN_BAR_HEIGHT, (avg / 255) * CANVAS_HEIGHT);
      // Smooth transition
      const height = prevHeights[i] + (targetHeight - prevHeights[i]) * SMOOTHING;
      prevHeights[i] = height;

      const x = i * (BAR_WIDTH + BAR_GAP);
      const y = (CANVAS_HEIGHT - height) / 2;

      ctx.fillStyle = theme.colors.orange;
      fillRoundedBar(ctx, x, y, BAR_WIDTH, height, 2);
    }

    rafRef.current = requestAnimationFrame(draw);
  }, [analyserNode]);

  useEffect(() => {
    if (isPlaying && analyserNode) {
      rafRef.current = requestAnimationFrame(draw);
    } else {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Draw idle state
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < BAR_COUNT; i++) {
          const x = i * (BAR_WIDTH + BAR_GAP);
          const y = (CANVAS_HEIGHT - MIN_BAR_HEIGHT) / 2;
          ctx.fillStyle = theme.colors.orange;
          fillRoundedBar(ctx, x, y, BAR_WIDTH, MIN_BAR_HEIGHT, 2);
        }
        prevHeightsRef.current = new Array(BAR_COUNT).fill(MIN_BAR_HEIGHT);
      }
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isPlaying, analyserNode, draw]);

  return (
    <Container>
      <Canvas ref={canvasRef} width={canvasWidth} height={CANVAS_HEIGHT} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Canvas = styled.canvas`
  display: block;
`;

export default WaveformVisualizer;
